import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { X, MoreHorizontal, Edit2, Trash2, Lightbulb, TrendingUp, Check, ShoppingBag, RotateCcw } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { getExpirationDisplay } from '../../lib/patternEngine'
import { getStorageGuide } from '../../lib/storageGuide'
import { usePreferences } from '../../hooks/usePreferences'
import { setItemTotalQuantity } from '../../hooks/useBatchOps'
import StoreInput from '../shared/StoreInput'
import PriceHistoryChart from './PriceHistoryChart'
import type { EditableItem } from './AddItemModal'
import { LOCATIONS, type LocationId } from '../../constants/locations'
import {
  estimateCurrentQuantity,
  getQuantityStatus,
} from '../../lib/quantityCalculator'

// ─── Types ────────────────────────────────────────────────────────────────────

type Status = 'green' | 'amber' | 'red' | 'learning' | 'used'

export interface DetailItem extends EditableItem {
  household_id:            string
  last_confirmed_at:       string | null
  avg_restock_cycle_days:  number | null
  confirmation_count:      number
  status:                  Status
  daysRemaining:           number
  current_expiration_date: string | null
  track_expiration:        boolean
  status_override:         string | null
  location?:               string | null
  current_quantity?:       number | null
  quantity_updated_at?:    string | null
  estimated_daily_use?:    number | null
  quantity_unit?:          string | null
  notes?:                  string | null
  freshness_category?:     string | null
}

interface ItemBatch {
  id:           string
  quantity:     number
  expiry_date:  string | null
  status:       string
  created_at:   string
  purchased_at: string | null
  store_name:   string | null
  source:       string | null
}

interface Props {
  item:         DetailItem
  onClose:      () => void
  onEdit:       () => void
  onDeleted:    (itemName: string) => void
  onMarkAsOut?: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Semantic palette mapping for status pills
const STATUS_STYLES: Record<Status, { bg: string; fg: string; label: string }> = {
  green:    { bg: 'bg-ok-bg',           fg: 'text-forest',     label: 'Good'      },
  amber:    { bg: 'bg-warn-bg',         fg: 'text-terracotta', label: 'Buy soon'  },
  red:      { bg: 'bg-critical-bg',     fg: 'text-critical',   label: 'Need now'  },
  learning: { bg: 'bg-cream-warm',      fg: 'text-ink-muted',  label: 'Learning'  },
  used:     { bg: 'bg-border-hairline', fg: 'text-ink-muted',  label: 'Used up'   },
}

// Priority dot color per status
const STATUS_DOT: Record<Status, string> = {
  green:    'bg-forest',
  amber:    'bg-terracotta',
  red:      'bg-critical',
  learning: 'bg-ink-muted',
  used:     'bg-border-avatar',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function formatDateShort(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function getBatchExpiryColor(iso: string | null): string {
  if (!iso) return '#9A9491'
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)
  if (days < 0)  return '#C25B4E'
  if (days <= 3) return '#E07B39'
  if (days <= 7) return '#D4A843'
  return '#6B9E78'
}

function getBatchExpiryLabel(iso: string | null): string {
  if (!iso) return 'No expiry'
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)
  if (days < 0)   return 'Expired'
  if (days === 0) return 'Expires today'
  if (days === 1) return 'Expires tomorrow'
  return `Exp ${formatDateShort(iso)}`
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs text-ink-muted">{label}</span>
      <span className="text-sm font-medium text-ink">{value}</span>
    </div>
  )
}

// ─── ItemDetailSheet ──────────────────────────────────────────────────────────

export default function ItemDetailSheet({ item, onClose, onEdit, onDeleted, onMarkAsOut }: Props) {
  const queryClient   = useQueryClient()
  const today         = new Date().toISOString().split('T')[0]

  const [toast,           setToast]           = useState<string | null>(null)
  const [showOverflow,    setShowOverflow]    = useState(false)
  const [confirmDelete,   setConfirmDelete]   = useState(false)
  const [deleting,        setDeleting]        = useState(false)
  const [editingExpiry,   setEditingExpiry]   = useState(false)
  const [newExpiryDate,   setNewExpiryDate]   = useState('')
  const [savingExpiry,    setSavingExpiry]    = useState(false)
  const [trackExpiration, setTrackExpiration] = useState(item.track_expiration ?? true)

  // Batch state. Auto-expand when 2+ batches exist on first load — single
  // batch is just "the item," no value in showing the breakdown. Once the
  // user manually collapses, we don't fight them: the auto-expand only
  // fires once per mount via the autoExpandedRef.
  const [showBatches,    setShowBatches]    = useState(false)
  const autoExpandedRef                     = useRef(false)
  const [addingBatch,    setAddingBatch]    = useState(false)
  const [batchQty,       setBatchQty]       = useState(1)
  const [batchExpiry,    setBatchExpiry]    = useState('')
  const [savingBatch,    setSavingBatch]    = useState(false)
  const [confirmBatchId, setConfirmBatchId] = useState<string | null>(null)

  // Batches query — selects store/source/purchased_at so the row labels
  // can show "Trader Joe's · Apr 24" rather than the older "Unknown source".
  // FIFO order (earliest expiry first) so the soonest-to-expire batch shows
  // at the top, matching what the qty-pill consumes first.
  const { data: batches = [], refetch: refetchBatches } = useQuery({
    queryKey: ['item-batches', item.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('item_batches')
        .select('id, quantity, expiry_date, status, created_at, purchased_at, store_name, source')
        .eq('item_id', item.id)
        .eq('status', 'active')
        .order('expiry_date', { ascending: true, nullsFirst: false })
        .order('created_at',  { ascending: true })
      return (data ?? []) as ItemBatch[]
    },
  })

  const totalAtHome = batches.reduce((sum, b) => sum + b.quantity, 0)

  // Auto-expand the batches section the first time we observe 2+ batches.
  // Only fires once per mount so a user who collapses it stays collapsed
  // even if more batches arrive afterwards.
  useEffect(() => {
    if (autoExpandedRef.current) return
    if (batches.length >= 2) {
      autoExpandedRef.current = true
      setShowBatches(true)
    }
  }, [batches.length])

  async function handleAddBatch() {
    if (batchQty < 1) return
    setSavingBatch(true)
    try {
      await supabase.from('item_batches').insert({
        item_id:      item.id,
        household_id: item.household_id,
        quantity:     batchQty,
        expiry_date:  batchExpiry || null,
        status:       'active',
        source:       'manual_add',
        purchased_at: new Date().toISOString(),
      })

      // Update item expiry to earliest batch expiry
      if (batchExpiry) {
        const currentExpiry = item.current_expiration_date
        if (!currentExpiry || batchExpiry < currentExpiry) {
          await supabase
            .from('pantry_items')
            .update({ current_expiration_date: batchExpiry })
            .eq('id', item.id)
        }
      }

      setBatchQty(1)
      setBatchExpiry('')
      setAddingBatch(false)
      refetchBatches()
      showToast('Batch added')
    } catch (err) {
      console.error('[ItemDetailSheet] add batch:', err)
    } finally {
      setSavingBatch(false)
    }
  }

  async function handleBatchAction(batchId: string, action: 'used' | 'delete') {
    if (action === 'used') {
      await supabase
        .from('item_batches')
        .update({ status: 'used' })
        .eq('id', batchId)
    } else {
      await supabase
        .from('item_batches')
        .delete()
        .eq('id', batchId)
    }
    setConfirmBatchId(null)

    // After removal, sync item expiry to earliest remaining active batch
    const { data: remaining } = await supabase
      .from('item_batches')
      .select('expiry_date')
      .eq('item_id', item.id)
      .eq('status', 'active')
      .not('expiry_date', 'is', null)
      .order('expiry_date', { ascending: true })
      .limit(1)

    const earliestExpiry = remaining?.[0]?.expiry_date ?? null
    await supabase
      .from('pantry_items')
      .update({ current_expiration_date: earliestExpiry })
      .eq('id', item.id)

    refetchBatches()
    queryClient.invalidateQueries({ queryKey: ['pantry-items', item.household_id] })
    showToast(action === 'used' ? 'Marked as used' : 'Batch removed')
  }

  async function handleSaveExpiry() {
    if (!newExpiryDate) return
    setSavingExpiry(true)
    try {
      const { error: itemError } = await supabase
        .from('pantry_items')
        .update({ current_expiration_date: newExpiryDate })
        .eq('id', item.id)

      if (itemError) {
        console.error('[ItemDetailSheet] expiry update error:', itemError)
        showToast('Failed to save date')
        return
      }

      // Update existing unconfirmed event, or insert a new one
      const { data: existingEvent } = await supabase
        .from('expiration_events')
        .select('id')
        .eq('item_id', item.id)
        .is('confirmed_at', null)
        .maybeSingle()

      if (existingEvent) {
        await supabase
          .from('expiration_events')
          .update({ expiration_date: newExpiryDate })
          .eq('id', existingEvent.id)
      } else {
        await supabase
          .from('expiration_events')
          .insert({
            household_id:    item.household_id,
            item_id:         item.id,
            expiration_date: newExpiryDate,
          })
      }

      setEditingExpiry(false)
      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      queryClient.invalidateQueries({ queryKey: ['expiration-waste'] })
      showToast('Expiration date saved')
    } catch (err) {
      console.error('[ItemDetailSheet] save expiry:', err)
    } finally {
      setSavingExpiry(false)
    }
  }

  async function handleClearExpiry() {
    setSavingExpiry(true)
    try {
      await supabase
        .from('pantry_items')
        .update({ current_expiration_date: null })
        .eq('id', item.id)

      await supabase
        .from('expiration_events')
        .delete()
        .eq('item_id', item.id)
        .is('confirmed_at', null)

      setEditingExpiry(false)
      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      queryClient.invalidateQueries({ queryKey: ['expiration-waste'] })
      showToast('Expiration date removed')
    } catch (err) {
      console.error('[ItemDetailSheet] clear expiry:', err)
    } finally {
      setSavingExpiry(false)
    }
  }

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 2000)
  }

  async function handleStatusOverride(override: 'used' | 'out' | null) {
    const { error } = await supabase
      .from('pantry_items')
      .update({ status_override: override })
      .eq('id', item.id)

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
      const msg = override === 'used'
        ? 'Marked as used'
        : override === 'out'
        ? 'Marked as out'
        : 'Status reset to automatic'
      showToast(msg)
      onClose()
    }
  }

  async function handleToggleExpiration() {
    const newValue = !trackExpiration
    setTrackExpiration(newValue)
    await supabase
      .from('pantry_items')
      .update({
        track_expiration:        newValue,
        ...(newValue ? {} : { current_expiration_date: null }),
      })
      .eq('id', item.id)
    queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
  }

  const { isPremium } = usePreferences()

  const [itemLocation,  setItemLocation]  = useState<LocationId>(
    (item.location as LocationId | null | undefined) ?? 'pantry'
  )
  const [adjustedQty, setAdjustedQty] = useState<number | null>(null)

  // Quantity derived values
  const estimatedQuantity = item.current_quantity != null
    ? estimateCurrentQuantity(
        item.current_quantity,
        item.quantity_updated_at ? new Date(item.quantity_updated_at) : new Date(),
        item.avg_restock_cycle_days ?? null,
        item.estimated_daily_use   ?? null,
      )
    : null

  const quantityStatus = estimatedQuantity != null
    ? getQuantityStatus(estimatedQuantity, item.current_quantity!)
    : null

  async function handleLocationChange(locationId: LocationId) {
    setItemLocation(locationId)
    await supabase
      .from('pantry_items')
      .update({ location: locationId })
      .eq('id', item.id)
    queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
  }

  async function saveQuantityAdjustment() {
    if (adjustedQty === null) return

    // Audit log first — captures user intent regardless of how the batch
    // ops resolve below.
    await supabase
      .from('quantity_adjustments')
      .insert({
        household_id:      item.household_id,
        item_id:           item.id,
        previous_quantity: estimatedQuantity,
        new_quantity:      adjustedQty,
        reason:            'manual',
      })

    // Apply through batches (FIFO consume on decrement, synthetic batch on
    // increment). The Phase 1 trigger derives current_quantity from active
    // batches — never write current_quantity directly.
    await setItemTotalQuantity({
      itemId:      item.id,
      householdId: item.household_id,
      newTotal:    adjustedQty,
    })

    queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
    queryClient.invalidateQueries({ queryKey: ['item-batches'] })
    showToast('Quantity updated')
    setAdjustedQty(null)
  }

  async function handleUnitChange(unit: string) {
    await supabase
      .from('pantry_items')
      .update({ quantity_unit: unit })
      .eq('id', item.id)
    queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
  }

  const [editingNotes, setEditingNotes] = useState(false)
  const [notesValue,   setNotesValue]   = useState('')

  async function handleSaveNotes() {
    await supabase
      .from('pantry_items')
      .update({ notes: notesValue.trim() || null })
      .eq('id', item.id)
    queryClient.invalidateQueries({ queryKey: ['pantry-items'] })
    setEditingNotes(false)
    showToast('Note saved')
  }

  const [showMore,         setShowMore]         = useState(false)
  const [showPriceInput,   setShowPriceInput]   = useState(false)
  const [newPrice,         setNewPrice]         = useState('')
  const [newStore,         setNewStore]         = useState('')
  const [savingPrice,      setSavingPrice]      = useState(false)
  const [showPriceHistory, setShowPriceHistory] = useState(false)

  // Fetch last 5 logged prices for this item from the canonical price book.
  // Confirmations remain the audit log; store_prices is the read surface for
  // Spending / Savings / List / StorePrices and now this sheet — single source
  // of truth for "what was the recent price?". The `enabled: !!item.id` +
  // `staleTime: 0` combo prevents the previous item's cached data from leaking
  // into the next item's open (e.g. ghost "$3.69 avg" on a fresh item).
  const { data: priceHistory = [], refetch: refetchPrices } = useQuery({
    queryKey:  ['priceHistory', 'store_prices', item.id],
    enabled:   !!item.id,
    staleTime: 0,
    queryFn:  async () => {
      const { data } = await supabase
        .from('store_prices')
        .select('id, price, recorded_at, store_name, was_on_sale')
        .eq('item_id', item.id)
        .not('price', 'is', null)
        .order('recorded_at', { ascending: false })
        .limit(5)
      return (data ?? []) as Array<{
        id:           string
        price:        number
        recorded_at:  string
        store_name:   string | null
        was_on_sale:  boolean | null
      }>
    },
  })

  async function handleSavePrice() {
    const parsed = parseFloat(newPrice)
    if (isNaN(parsed) || parsed <= 0) return
    setSavingPrice(true)
    try {
      await supabase
        .from('confirmations')
        .insert({
          household_id: item.household_id,
          item_id:      item.id,
          price_paid:   parsed,
          store_name:   newStore.trim() || null,
          source:       'manual_price',
          confirmed_at: new Date().toISOString(),
        })
      // If a store was also provided, record in store_prices + user_stores
      const store = newStore.trim()
      if (store) {
        await supabase.from('store_prices').insert({
          household_id: item.household_id,
          item_id:      item.id,
          store_name:   store,
          price:        parsed,
          recorded_at:  new Date().toISOString(),
        })
        await supabase
          .from('user_stores')
          .upsert(
            { household_id: item.household_id, store_name: store, last_visited: new Date().toISOString().split('T')[0] },
            { onConflict: 'household_id,store_name' },
          )
        await supabase.rpc('increment_store_visit', {
          p_household_id: item.household_id,
          p_store_name:   store,
        })
      }

      setNewPrice('')
      setNewStore('')
      setShowPriceInput(false)
      refetchPrices()
      // Partial-key invalidation: clears any cached priceHistory entry for
      // any item, defending against cross-item cache leak in case this sheet
      // is reused for a different item before the next render.
      queryClient.invalidateQueries({ queryKey: ['priceHistory'] })
      queryClient.invalidateQueries({ queryKey: ['confirmations', item.household_id] })
      queryClient.invalidateQueries({ queryKey: ['store-prices', item.household_id] })
      showToast('Price logged')
    } catch (err) {
      console.error('[ItemDetailSheet] save price:', err)
    } finally {
      setSavingPrice(false)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    const { error } = await supabase
      .from('pantry_items')
      .update({ is_active: false })
      .eq('id', item.id)

    if (!error) {
      queryClient.invalidateQueries({ queryKey: ['pantry-items', item.household_id] })
      onDeleted(item.name)
    } else {
      setDeleting(false)
    }
  }

  // Days remaining display
  const daysRounded    = Math.max(0, Math.round(item.daysRemaining))
  const daysLabel      = `${daysRounded} day${daysRounded === 1 ? '' : 's'} left`
  const showDaysRemaining = item.status === 'green' || item.status === 'amber'

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end items-center">

      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />

      {/* Sheet */}
      <motion.div
        className="relative w-full max-w-[480px] rounded-t-[28px] bg-cream max-h-[92dvh] overflow-y-auto shadow-2xl"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 sticky top-0 bg-cream z-10">
          <div className="h-1 w-10 rounded-full bg-border-avatar" />
        </div>

        {/* Editorial masthead */}
        <div className="px-5 pt-2 pb-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                In your pantry
              </div>
              <h2 className="font-serif text-[22px] font-normal leading-[1.1] tracking-[-0.015em] text-ink break-words">
                {item.name}
              </h2>
              <p className="font-serif text-[12px] italic text-ink-muted capitalize mt-1">
                {item.display_category.replace(/_/g, ' ')}
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] font-medium ${STATUS_STYLES[item.status].bg} ${STATUS_STYLES[item.status].fg}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[item.status]}`} />
                  {STATUS_STYLES[item.status].label}
                </span>
                {showDaysRemaining && (
                  <span className="font-serif text-[11px] italic text-ink-muted">
                    {daysLabel}
                  </span>
                )}
                {totalAtHome > 0 && (
                  <span className="font-serif text-[11px] italic text-forest">
                    {totalAtHome} at home
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowOverflow(s => !s)}
              aria-label="More actions"
              className="h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
            >
              <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted"
            >
              <X className="h-[14px] w-[14px]" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Overflow menu */}
        {showOverflow && (
          <div className="mx-5 mb-4 rounded-[14px] border border-border-sand bg-paper overflow-hidden">
            <button
              type="button"
              onClick={() => { setShowOverflow(false); onEdit() }}
              className="w-full flex items-center gap-3 px-4 py-3 border-b border-border-hairline text-left"
            >
              <Edit2 className="h-[14px] w-[14px] text-ink-muted" strokeWidth={1.7} />
              <span className="font-serif text-[14px] text-ink">Edit details</span>
            </button>
            <button
              type="button"
              onClick={() => { setShowOverflow(false); setConfirmDelete(true) }}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
            >
              <Trash2 className="h-[14px] w-[14px] text-critical" strokeWidth={1.7} />
              <span className="font-serif text-[14px] italic text-critical">Delete from pantry</span>
            </button>
          </div>
        )}

        <div className="px-5 py-4 flex flex-col gap-4">

          {/* At home — batch tracking */}
          <div className="border border-border-sand rounded-2xl overflow-hidden">
            <button
              onClick={() => setShowBatches(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink">At home</span>
                {totalAtHome > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F0EDE8] text-ink-muted">
                    {totalAtHome} total
                  </span>
                )}
              </div>
              <span className="text-xs text-ink-muted">{showBatches ? '▲' : '▼'}</span>
            </button>

            {showBatches && (
              <div className="border-t border-border-hairline px-4 py-3 flex flex-col gap-3">
                {/* Batch list */}
                {batches.length === 0 && !addingBatch && (
                  <p className="text-xs text-[#B0A99F] text-center py-2">
                    No batches tracked yet
                  </p>
                )}

                {batches.map(batch => (
                  <div key={batch.id}>
                    {confirmBatchId === batch.id ? (
                      <div className="bg-critical-bg rounded-xl p-3 flex flex-col gap-2">
                        <p className="text-xs font-medium text-ink text-center">
                          What happened to {batch.quantity} {batch.quantity === 1 ? 'unit' : 'units'}?
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleBatchAction(batch.id, 'used')}
                            className="flex-1 py-2 rounded-lg bg-forest text-white text-xs font-semibold"
                          >
                            Used it
                          </button>
                          <button
                            onClick={() => handleBatchAction(batch.id, 'delete')}
                            className="flex-1 py-2 rounded-lg bg-[#C25B4E] text-white text-xs font-semibold"
                          >
                            Remove
                          </button>
                          <button
                            onClick={() => setConfirmBatchId(null)}
                            className="px-3 py-2 rounded-lg border border-border-sand text-xs text-ink-muted"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-paper rounded-xl px-3 py-2.5 border border-border-hairline">
                        <div>
                          <p className="text-sm font-semibold text-ink">
                            {batch.quantity} {batch.quantity === 1 ? 'unit' : 'units'}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: getBatchExpiryColor(batch.expiry_date) }}>
                            {getBatchExpiryLabel(batch.expiry_date)}
                          </p>
                          {(() => {
                            // Source line: store name when known, otherwise a friendly
                            // tag derived from the batch's source column.
                            const sourceLabel =
                              batch.store_name?.trim()
                                ? batch.store_name.trim()
                                : batch.source === 'manual_adjust' ? 'Added manually'
                                : batch.source === 'manual_add'    ? 'Added manually'
                                : batch.source === 'barcode_scan'  ? 'Scanned'
                                : batch.source === 'unpack'        ? 'From unpack'
                                : batch.source === 'legacy'        ? 'Existing batch'
                                : null
                            const dateRaw = batch.purchased_at ?? batch.created_at
                            const dateLabel = dateRaw ? formatDateShort(dateRaw.slice(0, 10)) : null
                            if (!sourceLabel && !dateLabel) return null
                            return (
                              <p className="text-[10px] italic text-ink-muted mt-0.5">
                                {sourceLabel}
                                {sourceLabel && dateLabel ? ' · ' : ''}
                                {dateLabel}
                              </p>
                            )
                          })()}
                        </div>
                        <button
                          onClick={() => setConfirmBatchId(batch.id)}
                          className="text-xs text-ink-muted hover:text-critical transition-colors px-2 py-1"
                        >
                          ···
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add batch form */}
                {addingBatch ? (
                  <div className="bg-cream-warm rounded-xl p-3 flex flex-col gap-3">
                    <p className="text-xs font-semibold text-ink">Add a batch</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setBatchQty(q => Math.max(1, q - 1))}
                        className="w-9 h-9 rounded-full bg-[#E8E2DC] text-ink font-bold
                                   flex items-center justify-center shrink-0"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        value={batchQty}
                        onChange={e => setBatchQty(Math.max(1, parseInt(e.target.value) || 1))}
                        className="flex-1 text-center py-2 rounded-xl border border-border-sand
                                   text-sm font-bold text-ink outline-none focus:border-[#6B9E78]"
                      />
                      <button
                        onClick={() => setBatchQty(q => q + 1)}
                        className="w-9 h-9 rounded-full bg-[#E8E2DC] text-ink font-bold
                                   flex items-center justify-center shrink-0"
                      >
                        +
                      </button>
                    </div>
                    <div>
                      <p className="text-xs text-ink-muted mb-1.5">Expiry date (optional)</p>
                      <input
                        type="date"
                        value={batchExpiry}
                        min={today}
                        onChange={e => setBatchExpiry(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-border-sand
                                   text-sm text-ink outline-none focus:border-[#6B9E78]"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddBatch}
                        disabled={savingBatch}
                        className="flex-1 py-2 rounded-xl bg-forest text-white text-xs font-semibold disabled:opacity-50"
                      >
                        {savingBatch ? 'Saving…' : 'Add'}
                      </button>
                      <button
                        onClick={() => { setAddingBatch(false); setBatchQty(1); setBatchExpiry('') }}
                        className="px-4 py-2 rounded-xl border border-border-sand text-xs text-ink-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingBatch(true)}
                    className="w-full py-2.5 rounded-xl border border-dashed border-[#6B9E78]
                               text-xs font-semibold text-forest"
                  >
                    + Add batch
                  </button>
                )}
              </div>
            )}
          </div>

          {/* More details toggle */}
          <button
            onClick={() => setShowMore(v => !v)}
            className="w-full py-2.5 rounded-xl text-xs font-semibold text-forest
                       flex items-center justify-center gap-1.5"
            style={{ border: '1px solid rgba(107,158,120,0.3)', background: 'rgba(107,158,120,0.05)' }}
          >
            {showMore ? '↑ Less details' : '↓ More details'}
          </button>

          {/* Detail rows (collapsed by default) */}
          {showMore && (
          <div className="bg-paper rounded-2xl border border-border-hairline divide-y divide-border-hairline overflow-hidden">
            <Row label="Last purchase"      value={formatDate(item.last_confirmed_at)} />
            <Row
              label="Shopping rhythm"
              value={item.avg_restock_cycle_days
                ? `${Math.round(item.avg_restock_cycle_days)} days`
                : 'Not yet calculated'}
            />

            <Row label="Purchases"          value={String(item.confirmation_count)} />
          </div>
          )}

          {/* Location selector — collapsed */}
          {showMore && <div className="border-t border-border-sand pt-4">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
              Storage location
            </p>
            <div className="grid grid-cols-2 gap-2">
              {Object.values(LOCATIONS).map(loc => (
                <button
                  key={loc.id}
                  onClick={() => handleLocationChange(loc.id as LocationId)}
                  className="flex items-center justify-center px-3 py-2.5 rounded-xl text-sm font-medium
                             transition-colors text-center"
                  style={{
                    background: itemLocation === loc.id ? loc.colorLight : '#F7F5F2',
                    color:      itemLocation === loc.id ? loc.color      : '#7A7468',
                    border:     itemLocation === loc.id
                      ? `1.5px solid ${loc.color}60`
                      : '1.5px solid transparent',
                  }}
                >
                  <span>{loc.label}</span>
                </button>
              ))}
            </div>
          </div>}

          {/* Storage guide */}
          {(() => {
            const storageInfo = getStorageGuide(
              item.freshness_category || 'pantry_staple',
              item.location || 'pantry',
            )
            const locLabel =
              item.location === 'fridge'   ? 'In your fridge'   :
              item.location === 'freezer'  ? 'In your freezer'  :
              item.location === 'cabinets' ? 'In your cabinets' :
              'In your pantry'

            return (
              <div className="border-t border-border-sand pt-4 mt-4">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
                  Storage guide
                </p>
                <div className="bg-cream rounded-2xl p-4 mb-3">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2">
                    {locLabel}
                  </div>
                  <p className="text-xs text-ink-muted leading-relaxed">
                    {storageInfo.currentLocationTip}
                  </p>
                  {storageInfo.maxDaysInCurrentLocation > 0 && (
                    <p className="text-xs text-forest font-medium mt-2">
                      Lasts up to{' '}
                      {storageInfo.maxDaysInCurrentLocation > 30
                        ? `${Math.round(storageInfo.maxDaysInCurrentLocation / 30)} months`
                        : `${storageInfo.maxDaysInCurrentLocation} days`}
                    </p>
                  )}
                </div>
                <div className="flex items-start gap-2.5 rounded-[12px] bg-cream-warm p-3">
                  <Lightbulb className="h-[14px] w-[14px] text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.7} />
                  <p className="font-serif text-[13px] italic text-ink leading-[1.45]">
                    {storageInfo.tip}
                  </p>
                </div>
              </div>
            )
          })()}

          {/* Price section */}
          <div className="border-t border-border-sand pt-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">Price</p>
              {!showPriceInput && (
                <button
                  onClick={() => setShowPriceInput(true)}
                  className="text-xs font-semibold text-forest hover:underline"
                >
                  + Log a price
                </button>
              )}
            </div>

            {/* Avg price */}
            {priceHistory.length > 0 && (
              <div className="flex items-baseline gap-1.5 mb-3">
                <span className="text-2xl font-bold text-ink">
                  ${(priceHistory.reduce((s, c) => s + c.price, 0) / priceHistory.length).toFixed(2)}
                </span>
                <span className="text-xs text-ink-muted">avg paid</span>
              </div>
            )}

            {/* Price log form */}
            {showPriceInput && (
              <div className="bg-paper rounded-2xl border border-border-sand p-4 mb-3 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-ink-muted">$</span>
                  <input
                    type="number"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={newPrice}
                    onChange={e => setNewPrice(e.target.value)}
                    className="flex-1 border border-border-sand rounded-lg px-3 py-2 text-sm
                               text-ink focus:outline-none focus:border-[#6B9E78]"
                  />
                </div>
                <StoreInput
                  value={newStore}
                  onChange={setNewStore}
                  householdId={item.household_id}
                  placeholder="Store (optional)"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowPriceInput(false); setNewPrice(''); setNewStore('') }}
                    className="flex-1 py-2 rounded-xl border border-border-sand text-ink-muted
                               text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSavePrice}
                    disabled={savingPrice || !newPrice}
                    className="flex-1 py-2 rounded-xl bg-forest text-white
                               text-sm font-semibold disabled:opacity-50"
                  >
                    {savingPrice ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            )}

            {/* Price history list — only when expanded AND prices exist */}
            {showMore && priceHistory.length > 0 && (
              <>
                <div className="bg-paper rounded-2xl border border-border-hairline divide-y divide-border-hairline overflow-hidden">
                  {priceHistory.map((c) => (
                    c.was_on_sale ? (
                      /* Sale record — store_prices doesn't carry the original
                         price, so we just badge it as sale with the date+store */
                      <div key={c.id} className="flex items-center justify-between px-4 py-2">
                        <div>
                          <span className="text-xs text-forest font-medium">Sale price</span>
                          <p className="text-[10px] text-[#B0A99F]">
                            {formatDate(c.recorded_at)}
                            {c.store_name && ` · ${c.store_name}`}
                          </p>
                        </div>
                        <span className="text-xs font-semibold text-forest">
                          ${c.price.toFixed(2)}/ea
                        </span>
                      </div>
                    ) : (
                      /* Regular record */
                      <div key={c.id} className="flex items-center justify-between px-4 py-3">
                        <div>
                          <span className="text-xs text-ink-muted">{formatDate(c.recorded_at)}</span>
                          {c.store_name && (
                            <span className="text-xs text-[#B0A99F] ml-1.5">· {c.store_name}</span>
                          )}
                        </div>
                        <span className="text-sm font-medium text-ink">
                          ${c.price.toFixed(2)}
                        </span>
                      </div>
                    )
                  ))}
                </div>

                {/* Per-store breakdown */}
                {(() => {
                  const storeMap = priceHistory.reduce((acc, record) => {
                    if (!record.store_name) return acc
                    if (!acc[record.store_name]) acc[record.store_name] = []
                    acc[record.store_name].push(record.price)
                    return acc
                  }, {} as Record<string, number[]>)

                  const storeEntries = Object.entries(storeMap)
                    .sort(([, a], [, b]) =>
                      a.reduce((s, p) => s + p, 0) / a.length -
                      b.reduce((s, p) => s + p, 0) / b.length
                    )

                  if (storeEntries.length < 2) return null

                  return (
                    <div className="mt-3">
                      <p className="text-xs text-ink-muted uppercase tracking-wide font-semibold mb-2">
                        By store
                      </p>
                      {storeEntries.map(([store, prices]) => (
                        <div key={store} className="flex items-center justify-between py-2">
                          <span className="text-sm text-ink">{store}</span>
                          <div className="text-right">
                            <span className="text-sm font-semibold text-ink">
                              ${(prices.reduce((s, p) => s + p, 0) / prices.length).toFixed(2)}
                            </span>
                            <span className="text-xs text-[#B0A99F] ml-1">avg</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </>
            )}

            {/* Empty-state — purely a function of price count, independent of
                expansion. Suppressed while the user is actively entering a
                price so the form isn't fighting the empty copy. */}
            {priceHistory.length === 0 && !showPriceInput && (
              <p className="text-xs text-[#B0A99F]">No prices logged yet</p>
            )}

            {/* Price history chart — collapsed */}
            {showMore && priceHistory.length >= 2 && (
              <>
                <button
                  onClick={() => setShowPriceHistory(v => !v)}
                  className="w-full mt-3 py-2 rounded-xl text-xs font-medium text-forest
                             flex items-center justify-center gap-1.5"
                  style={{ border: '1px solid rgba(107,158,120,0.3)' }}
                >
                  <TrendingUp className="h-[12px] w-[12px]" strokeWidth={2} />
                  {showPriceHistory
                    ? 'Hide price history'
                    : `View price history (${priceHistory.length} records)`}
                </button>
                {showPriceHistory && (
                  <div className="mt-3">
                    <PriceHistoryChart
                      itemId={item.id}
                      itemName={item.name}
                      itemEmoji=""
                      householdId={item.household_id}
                      isPremium={isPremium}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Expiration date (hidden when tracking is off) */}
          {trackExpiration && (
            <div className="border-t border-border-sand pt-4 mt-4">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
                Expiration date
              </p>

              {!editingExpiry ? (
                <div className="flex items-center justify-between">
                  <div>
                    {item.current_expiration_date ? (
                      <div>
                        <p className="text-sm font-medium text-ink">
                          {new Date(item.current_expiration_date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </p>
                        {getExpirationDisplay(item.current_expiration_date) && (
                          <p className="text-xs mt-0.5"
                            style={{ color: getExpirationDisplay(item.current_expiration_date)?.color }}
                          >
                            {getExpirationDisplay(item.current_expiration_date)?.text}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#B0A99F]">No expiration date set</p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setNewExpiryDate(item.current_expiration_date ?? '')
                      setEditingExpiry(true)
                    }}
                    className="text-sm font-medium text-forest hover:opacity-70 transition-opacity"
                  >
                    {item.current_expiration_date ? 'Edit' : '+ Add'}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="date"
                    value={newExpiryDate}
                    onChange={e => setNewExpiryDate(e.target.value)}
                    min={today}
                    className="w-full px-4 py-2.5 rounded-xl border border-border-sand
                               text-sm text-ink outline-none focus:border-[#6B9E78]"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveExpiry}
                      disabled={savingExpiry || !newExpiryDate}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white
                                 disabled:opacity-50"
                      style={{ background: '#6B9E78' }}
                    >
                      {savingExpiry ? 'Saving…' : 'Save date'}
                    </button>
                    {item.current_expiration_date && (
                      <button
                        onClick={handleClearExpiry}
                        disabled={savingExpiry}
                        className="px-4 py-2.5 rounded-xl text-sm text-critical
                                   border border-[#C25B4E] disabled:opacity-50"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      onClick={() => setEditingExpiry(false)}
                      className="px-4 py-2.5 rounded-xl text-sm text-ink-muted
                                 border border-border-sand"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Track expiration toggle — collapsed */}
          {showMore && <div className="flex items-center justify-between py-4 border-t border-border-sand">
            <div>
              <p className="text-sm font-semibold text-ink">Track expiration date</p>
              <p className="text-xs text-ink-muted mt-0.5">Turn off for items that don't expire</p>
            </div>
            <button
              type="button"
              onClick={handleToggleExpiration}
              aria-pressed={trackExpiration}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                trackExpiration ? 'bg-forest' : 'bg-border-avatar'
              }`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-paper shadow-sm transition-transform duration-200 ${
                trackExpiration ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`} />
            </button>
          </div>}

          {/* Quantity section — collapsed */}
          {showMore && item.current_quantity != null && (
            <div className="border-t border-border-sand pt-4 mt-4">
              <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
                Quantity
              </p>

              {/* Large depletion bar */}
              {quantityStatus && (
                <div className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-ink-muted">Estimated remaining</span>
                    <span className="text-sm font-bold" style={{ color: quantityStatus.color }}>
                      ~{Math.round(estimatedQuantity || 0)}{' '}{item.quantity_unit || 'units'}
                    </span>
                  </div>
                  <div className="h-3 bg-cream-warm rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width:      `${quantityStatus.percentage || 0}%`,
                        background: quantityStatus.color,
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[10px] text-[#B0A99F]">Empty</span>
                    <span className="text-[10px] text-[#B0A99F]">
                      {item.current_quantity} bought
                    </span>
                  </div>
                </div>
              )}

              {/* Manual adjustment */}
              <p className="text-xs text-ink-muted mb-2">Adjust if estimate is wrong</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setAdjustedQty(Math.max(0, (adjustedQty ?? Math.round(estimatedQuantity || 0)) - 1))}
                  className="w-9 h-9 rounded-full bg-cream-warm text-ink
                             font-bold flex items-center justify-center"
                >
                  −
                </button>
                <input
                  type="number"
                  min="0"
                  value={adjustedQty ?? Math.round(estimatedQuantity || 0)}
                  onChange={e => setAdjustedQty(parseInt(e.target.value) || 0)}
                  className="flex-1 text-center py-2 rounded-xl border border-border-sand
                             text-sm font-bold text-ink outline-none focus:border-[#6B9E78]"
                />
                <button
                  onClick={() => setAdjustedQty((adjustedQty ?? Math.round(estimatedQuantity || 0)) + 1)}
                  className="w-9 h-9 rounded-full bg-cream-warm text-ink
                             font-bold flex items-center justify-center"
                >
                  +
                </button>
                <button
                  onClick={saveQuantityAdjustment}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-white"
                  style={{ background: '#6B9E78' }}
                >
                  Save
                </button>
              </div>

              {/* Unit selector */}
              <div className="flex gap-2 mt-3 flex-wrap">
                {['units', 'bottles', 'bags', 'boxes', 'cans', 'lbs', 'oz'].map(unit => (
                  <button
                    key={unit}
                    onClick={() => handleUnitChange(unit)}
                    className={`px-2.5 py-1 rounded-full text-xs transition-all ${
                      (item.quantity_unit || 'units') === unit
                        ? 'bg-forest text-white'
                        : 'bg-cream-warm text-ink-muted'
                    }`}
                  >
                    {unit}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quick update */}
          <div className="border-t border-border-sand pt-4">
            <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">
              Quick update
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleStatusOverride('used')}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                  item.status_override === 'used'
                    ? 'border-[#4A90D9] bg-[#E8F2FF]'
                    : 'border-border-sand bg-paper'
                }`}
              >
                <Check className="h-5 w-5 text-forest flex-shrink-0" strokeWidth={2} />
                <div className="text-left flex-1">
                  <p className="text-sm font-semibold text-ink">Mark as used</p>
                  <p className="text-xs text-ink-muted">I finished this — need to restock</p>
                </div>
                {item.status_override === 'used' && (
                  <Check className="h-4 w-4 text-[#4A90D9]" strokeWidth={2.5} />
                )}
              </button>

              <button
                onClick={() => handleStatusOverride('out')}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all ${
                  item.status_override === 'out'
                    ? 'border-[#C25B4E] bg-critical-bg'
                    : 'border-border-sand bg-paper'
                }`}
              >
                <ShoppingBag className="h-5 w-5 text-critical flex-shrink-0" strokeWidth={1.8} />
                <div className="text-left flex-1">
                  <p className="text-sm font-semibold text-ink">Mark as out</p>
                  <p className="text-xs text-ink-muted">Ran out — need to buy more</p>
                </div>
                {item.status_override === 'out' && (
                  <Check className="h-4 w-4 text-critical" strokeWidth={2.5} />
                )}
              </button>

              {item.status_override && (
                <button
                  onClick={() => handleStatusOverride(null)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-border-sand bg-transparent"
                >
                  <RotateCcw className="h-5 w-5 text-ink-muted flex-shrink-0" strokeWidth={1.8} />
                  <div className="text-left">
                    <p className="text-sm font-medium text-ink-muted">Let Pantry decide</p>
                    <p className="text-xs text-[#B0A99F]">Go back to automatic status</p>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Notes */}
          {!editingNotes ? (
            <button
              onClick={() => { setEditingNotes(true); setNotesValue(item.notes ?? '') }}
              className="w-full text-left px-4 py-3 rounded-2xl border border-dashed
                         border-border-sand text-sm text-[#B0A99F]"
            >
              {item.notes || '+ Add a note'}
            </button>
          ) : (
            <div className="space-y-2">
              <textarea
                value={notesValue}
                onChange={e => setNotesValue(e.target.value)}
                placeholder="e.g. Get the cage free ones"
                maxLength={120}
                rows={2}
                autoFocus
                className="w-full px-4 py-3 rounded-2xl border border-border-sand text-sm
                           text-ink outline-none resize-none focus:border-[#6B9E78]"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveNotes}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background: '#6B9E78' }}
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingNotes(false)}
                  className="px-4 py-2.5 rounded-xl text-sm text-ink-muted border border-border-sand"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Edit button */}
          <button
            onClick={onEdit}
            className="w-full py-3 rounded-xl border border-[#6B9E78] text-forest
                       text-sm font-semibold hover:bg-ok-bg transition-colors"
          >
            Edit item
          </button>

          {/* Mark as out */}
          {onMarkAsOut && item.status !== 'red' && (
            <button
              onClick={onMarkAsOut}
              className="w-full py-3 rounded-xl border border-[#C25B4E] text-critical
                         text-sm font-semibold hover:bg-critical-bg transition-colors"
            >
              Mark as out
            </button>
          )}

          {/* Delete */}
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full py-3 rounded-xl bg-critical-bg text-critical
                         text-sm font-semibold hover:bg-[#f5d8d5] transition-colors"
            >
              Remove from pantry
            </button>
          ) : (
            <div className="bg-critical-bg rounded-2xl p-4 flex flex-col gap-3">
              <p className="text-sm font-medium text-ink text-center">
                Remove {item.name} from your pantry?
              </p>
              <p className="text-xs text-ink-muted text-center">
                Your history is kept. This can't be undone from the app.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="flex-1 py-2.5 rounded-xl bg-paper border border-border-sand
                             text-ink-muted text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 py-2.5 rounded-xl bg-[#C25B4E] text-white
                             text-sm font-semibold disabled:opacity-60"
                >
                  {deleting ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          )}

        </div>
      </motion.div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-2xl
                        bg-[#2C2A26] text-white text-sm font-medium shadow-xl whitespace-nowrap">
          {toast}
        </div>
      )}
    </div>
  )
}
